import { router } from "@inertiajs/react";
import { Link } from "@inertiajs/react";
import { BackofficeLayout } from "@/components/layout/BackofficeLayout";
import { SearchInput } from "@/components/shared/SearchInput";
import { Pagination, PaginationMeta } from "@/components/shared/Pagination";
import { StageBadge } from '@/components/shared/StageBadge';
import { Button } from "@/components/ui/button";
import { FileText, Plus } from "lucide-react";
import { formatDate } from "@/lib/utils";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

type Formation = {
  id: string;
  legal_name: string;
  assumed_name: string | null;
  stage: string;
  entity_name: string;
  jurisdiction_code: string;
  account_name: string;
  account_slug: string;
  paid: boolean | null;
  created_at: string;
  compliance_officer_email?: string | null;
  support_officer_email?: string | null;
};

type Props = {
  formations: Formation[];
  search: string;
  assignedToMe?: boolean;
  canManageAssignments?: boolean;
  assignmentFilters?: {
    assigned_identity_id?: string;
    role: "either" | "compliance" | "support";
  };
  staffOptions?: { id: string; email_address: string }[];
  pagination: PaginationMeta;
};

export default function BackofficeFormationsIndex({
  formations,
  search,
  assignedToMe,
  canManageAssignments,
  assignmentFilters,
  staffOptions = [],
  pagination,
}: Props) {
  function updateAssignmentFilter(key: "assigned_identity_id" | "assignment_role", value: string) {
    const url = new URL(window.location.href)
    url.searchParams.delete('assigned_to_me')
    if (value === "all" || value === "either") {
      url.searchParams.delete(key)
    } else {
      url.searchParams.set(key, value)
    }
    router.get(url.toString(), {}, { preserveState: true, preserveScroll: true })
  }

  return (
    <BackofficeLayout
      title="Formations"
      breadcrumbs={[
        { label: "Backoffice", href: "/backoffice/dashboard" },
        { label: "Formations" },
      ]}
    >
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-xl font-semibold tracking-tight">Formations</h1>
          <p className="text-sm text-muted-foreground">
            {pagination.total} total
          </p>
        </div>
        <Button size="sm" asChild>
          <Link href="/backoffice/formations/new">
            <Plus className="w-3.5 h-3.5 mr-1" />
            New Formation
          </Link>
        </Button>
      </div>

      <div className="flex items-center gap-4">
        <div className="flex-1">
          <SearchInput value={search} placeholder="Search formations..." />
        </div>
        <div className="flex items-center space-x-2">
          <Button 
            variant={assignedToMe ? "default" : "outline"} 
            size="sm" 
            className="h-9 text-xs font-medium shadow-sm"
            onClick={() => {
              const url = new URL(window.location.href)
              if (assignedToMe) {
                url.searchParams.delete('assigned_to_me')
              } else {
                url.searchParams.set('assigned_to_me', 'true')
              }
              router.get(url.toString(), {}, { preserveState: true })
            }}
          >
            Assigned to me
          </Button>
        </div>
      </div>

      {canManageAssignments && (
        <div className="mt-3 flex flex-col gap-2 rounded-lg border p-3 md:flex-row md:items-center">
          <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Assignment filter</span>
          <Select
            value={assignmentFilters?.assigned_identity_id || "all"}
            onValueChange={value => updateAssignmentFilter("assigned_identity_id", value)}
          >
            <SelectTrigger className="h-8 md:w-72">
              <SelectValue placeholder="Staff member" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All staff</SelectItem>
              {staffOptions.map(option => (
                <SelectItem key={option.id} value={option.id}>{option.email_address}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select
            value={assignmentFilters?.role || "either"}
            onValueChange={value => updateAssignmentFilter("assignment_role", value)}
          >
            <SelectTrigger className="h-8 md:w-44">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="either">Either role</SelectItem>
              <SelectItem value="compliance">Compliance</SelectItem>
              <SelectItem value="support">Support</SelectItem>
            </SelectContent>
          </Select>
        </div>
      )}

      <div className="mt-4">
        {formations.length === 0 ? (
          <div className="border rounded-lg py-16 text-center">
            <FileText className="w-6 h-6 mx-auto mb-3 text-muted-foreground/30" />
            <h3 className="text-sm font-medium text-foreground mb-1">
              No formations found
            </h3>
            <p className="text-sm text-muted-foreground">
              Formations will appear here as users create them.
            </p>
          </div>
        ) : (
          <div className="border rounded-lg">
            {formations.map((f, i) => (
              <div
                key={f.id}
                className={`flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-muted/50 transition-colors ${i > 0 ? "border-t" : ""}`}
                onClick={() => router.visit(`/backoffice/formations/${f.id}`)}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-7 h-7 rounded-full bg-muted flex items-center justify-center shrink-0">
                    <span className="text-xs font-medium text-muted-foreground">
                      {f.legal_name.charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-foreground truncate">
                        {f.legal_name}
                      </span>
                      <StageBadge stage={f.stage} variant="formation" />
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {f.entity_name} · {f.jurisdiction_code} · {f.account_name}
                    </div>
                  </div>
                </div>
                <div className="ml-4 flex shrink-0 items-center gap-4">
                  {canManageAssignments && (
                    <div className="hidden text-right text-[11px] leading-4 text-muted-foreground md:block">
                      <div>CO: <span className="text-foreground">{f.compliance_officer_email || '-'}</span></div>
                      <div>SO: <span className="text-foreground">{f.support_officer_email || '-'}</span></div>
                    </div>
                  )}
                  <div className="text-xs text-muted-foreground">
                    {formatDate(f.created_at)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="mt-4">
        <Pagination pagination={pagination} />
      </div>
    </BackofficeLayout>
  );
}
